import React, { useCallback, useMemo, useState } from 'react';

import {
  Box,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import ColoredButton from 'components/ColoredButton';
import ErrorBoundary from 'components/ErrorBoundary';
import Loader from 'components/Loader';
import ManagePageHeader from 'components/ManagePageHeader/ManagePageHeader';
import TextButton from 'components/TextButton/TextButton';
import { LIA_CONCLUSION_OPTIONS, LIA_SAFEGUARD_OPTIONS } from 'config/api/lia/lia.constants';
import usePageTitle from 'hooks/usePageTitle';
import general_messages from 'messages/general_messages';
import processing_activities_messages from 'messages/processing_activities_messages';
import validation_messages from 'messages/validation_messages';
import PATHS from 'router/PATHS';
import userDataStorage from 'storages/userDataStorage';

import { canAccessPurposeLiaPage } from '../_shared/assetRopaReviewAccess';
import useStyles from '../ProcessingActivitiesAssetReviewsPage/ProcessingActivitiesAssetReviewsPage.styles';

import {
  buildCompletenessItems,
  buildValidationStatus,
  getLiaApiErrorMessage,
  isLiaReadOnly,
  toggleSafeguard,
  validatePurposeLiaForm,
} from './purposeLiaPage.helpers';
import SoftDeleteLiaModal from './SoftDeleteLiaModal';
import usePurposeLiaPage from './usePurposeLiaPage';

type RouteParams = {
  purposeId: string;
};

type LocationState = {
  returnPath?: string;
};

const PurposeLiaPage: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { purposeId } = useParams<RouteParams>();
  const { enqueueSnackbar } = useSnackbar();
  const userData = userDataStorage.get() || {};
  const organizationId = typeof userData.organization === 'number' ? userData.organization : undefined;
  const canAccess = canAccessPurposeLiaPage(userData);
  const messages = processing_activities_messages.purpose_lia;
  const [softDeleteOpen, setSoftDeleteOpen] = useState(false);

  usePageTitle(t(messages.page_title));

  const returnPath = location.state?.returnPath ?? PATHS.PROCESSING_ACTIVITIES;

  const navigateBack = useCallback(() => {
    history.push(returnPath);
  }, [history, returnPath]);

  const validationMessages = useMemo(
    () => ({
      required: t(validation_messages.required),
      vulnerableComment: t(messages.validation.vulnerable_comment),
    }),
    [messages, t],
  );

  const {
    canRetryLoad,
    detail,
    formValues,
    handleNavigation,
    handleSave,
    handleSoftDelete,
    isError,
    isLoading,
    isSaving,
    isSoftDeleting,
    loadErrorMessage,
    NavigationPrompt,
    patchFormValues,
    refetch,
    setValidationErrors,
    validationErrors,
  } = usePurposeLiaPage({
    purposeId: Number(purposeId),
    onNavigate: navigateBack,
    onSaveSuccess: () => {
      enqueueSnackbar(t(messages.toasts.saved), { variant: 'success' });
    },
    onSoftDeleteSuccess: () => {
      enqueueSnackbar(t(messages.toasts.soft_deleted), { variant: 'success' });
      navigateBack();
    },
    organizationId,
  });

  const readOnly = detail ? isLiaReadOnly(detail) : false;
  const completenessItems = formValues ? buildCompletenessItems(formValues) : [];
  const currentValidationStatus = useMemo(() => {
    if (!formValues || !detail) return 'invalid';
    return buildValidationStatus(formValues, detail.isDeleted);
  }, [formValues, detail]);

  const handleSaveClick = useCallback(async () => {
    if (!formValues) return;

    const errors = validatePurposeLiaForm(formValues, validationMessages);
    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      enqueueSnackbar(t(messages.validation.save_blocked), { variant: 'warning' });
      return;
    }

    try {
      await handleSave();
    } catch (error) {
      enqueueSnackbar(getLiaApiErrorMessage(error) || t(processing_activities_messages.asset_ropa.toasts.action_failed), {
        variant: 'error',
      });
    }
  }, [enqueueSnackbar, formValues, handleSave, messages, setValidationErrors, t, validationMessages]);

  const handleSoftDeleteConfirm = useCallback(
    async (reason: string) => {
      try {
        await handleSoftDelete(reason);
        setSoftDeleteOpen(false);
      } catch (error) {
        enqueueSnackbar(getLiaApiErrorMessage(error) || t(processing_activities_messages.asset_ropa.toasts.action_failed), {
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar, handleSoftDelete, t],
  );

  let content: React.ReactNode;

  if (!canAccess) {
    content = (
      <Typography color='textSecondary' variant='body2'>
        {t(processing_activities_messages.asset_ropa.forbidden_body)}
      </Typography>
    );
  } else if (isError) {
    content = (
      <Box className={styles.errorBanner}>
        <Typography color='error' variant='body2'>
          {loadErrorMessage || t(processing_activities_messages.asset_ropa.errors.load_failed)}
        </Typography>
        {canRetryLoad && <TextButton onClick={() => refetch()}>{t(general_messages.retry)}</TextButton>}
      </Box>
    );
  } else if (isLoading || !formValues || !detail) {
    content = <Loader inner />;
  } else {
    content = (
      <>
        <Box className={styles.reviewPanel}>
          <Typography variant='h3'>{detail.purposeContext || t(messages.page_title)}</Typography>
          <div className={styles.reviewMeta}>
            <span>
              <strong>{t(messages.status)}:</strong>{' '}
              <span
                className={`${styles.statusBadge} ${
                  currentValidationStatus === 'valid' ? styles.statusBadgeSuccess : styles.statusBadgeDanger
                }`}
              >
                {t(messages.validation_status[currentValidationStatus])}
              </span>
            </span>
            <span>
              <strong>{t(messages.revision)}:</strong> {detail.revision}
            </span>
          </div>
        </Box>

        <Typography className={styles.pageHelp} color='textSecondary' variant='body2'>
          {t(messages.truth_note)}
        </Typography>

        <div className={styles.twoColumnLayout}>
          <aside>
            <Box className={styles.sidePanel}>
              <div className={styles.reviewEyebrow}>{t(messages.side_panel.eyebrow)}</div>
              <Typography gutterBottom variant='h3'>
                {t(messages.side_panel.title)}
              </Typography>
              <div className={styles.fieldList}>
                <div>
                  <div className={styles.fieldLabel}>{t(messages.side_panel.purpose_context)}</div>
                  <div>{detail.purposeContext || '—'}</div>
                </div>
              </div>

              <Typography gutterBottom style={{ marginTop: 16 }} variant='h4'>
                {t(messages.side_panel.validation_rules)}
              </Typography>
              <Typography color='textSecondary' component='ul' variant='body2'>
                <li>{t(messages.side_panel.validation_rule_1)}</li>
                <li>{t(messages.side_panel.validation_rule_2)}</li>
                <li>{t(messages.side_panel.validation_rule_3)}</li>
              </Typography>

              <Box className={styles.errorBanner} style={{ backgroundColor: '#fff6f5', borderColor: '#efc8c6', marginTop: 16 }}>
                <Typography variant='body2'>{t(messages.side_panel.soft_delete_note)}</Typography>
              </Box>
            </Box>
          </aside>

          <section className={styles.mainPanel}>
            <Box className={styles.apaSection}>
              <div className={styles.reviewEyebrow}>SECTION 1</div>
              <Typography gutterBottom variant='h3'>
                {t(messages.sections.legitimate_interest.title)}
              </Typography>
              <TextField
                disabled={readOnly}
                fullWidth
                label={t(messages.sections.legitimate_interest.necessity)}
                margin='normal'
                minRows={4}
                multiline
                onChange={event => patchFormValues({ necessityAssessment: event.target.value })}
                value={formValues.necessityAssessment}
                variant='outlined'
              />
              <TextField
                disabled={readOnly}
                fullWidth
                label={t(messages.sections.impact.balancing)}
                margin='normal'
                minRows={4}
                multiline
                onChange={event => patchFormValues({ balancingAssessment: event.target.value })}
                value={formValues.balancingAssessment}
                variant='outlined'
              />
              <FormControl disabled={readOnly} fullWidth margin='normal' variant='outlined'>
                <InputLabel>{t(messages.sections.impact.vulnerable_involved)}</InputLabel>
                <Select
                  label={t(messages.sections.impact.vulnerable_involved)}
                  onChange={event => patchFormValues({ vulnerableDataSubjectsInvolved: event.target.value === 'yes' })}
                  value={formValues.vulnerableDataSubjectsInvolved ? 'yes' : 'no'}
                >
                  <MenuItem value='no'>{t(messages.sections.impact.vulnerable_no)}</MenuItem>
                  <MenuItem value='yes'>{t(messages.sections.impact.vulnerable_yes)}</MenuItem>
                </Select>
              </FormControl>
              <TextField
                disabled={readOnly}
                error={Boolean(validationErrors.vulnerableDataSubjectsComment)}
                fullWidth
                helperText={validationErrors.vulnerableDataSubjectsComment || t(messages.sections.impact.vulnerable_comment_help)}
                label={t(messages.sections.impact.vulnerable_comment)}
                margin='normal'
                minRows={3}
                multiline
                onChange={event => patchFormValues({ vulnerableDataSubjectsComment: event.target.value })}
                placeholder={t(messages.sections.impact.vulnerable_comment_placeholder)}
                value={formValues.vulnerableDataSubjectsComment}
                variant='outlined'
              />
            </Box>

            <Box className={styles.apaSection}>
              <div className={styles.reviewEyebrow}>SECTION 2</div>
              <FormControl component='fieldset' disabled={readOnly} fullWidth margin='normal'>
                <FormLabel component='legend'>
                  <Typography component='span' gutterBottom variant='h3'>
                    {t(messages.sections.safeguards.title)}
                  </Typography>
                </FormLabel>
                <div className={styles.safeguardsGrid}>
                  {LIA_SAFEGUARD_OPTIONS.map(option => (
                    <FormControlLabel
                      key={option}
                      className={styles.safeguardItem}
                      control={
                        <Checkbox
                          checked={formValues.safeguardsCodes.includes(option)}
                          disabled={readOnly}
                          onChange={() => patchFormValues({ safeguardsCodes: toggleSafeguard(formValues.safeguardsCodes, option) })}
                        />
                      }
                      label={
                        <span className={styles.safeguardLabel}>
                          <Typography className={styles.safeguardTitle} variant='body2'>
                            {t(messages.safeguards[option])}
                          </Typography>
                          <Typography className={styles.safeguardHelp} color='textSecondary' variant='caption'>
                            {t(messages.safeguard_help[option])}
                          </Typography>
                        </span>
                      }
                    />
                  ))}
                </div>
              </FormControl>
              <TextField
                disabled={readOnly}
                fullWidth
                label={t(messages.sections.safeguards.description)}
                margin='normal'
                minRows={4}
                multiline
                onChange={event => patchFormValues({ safeguardsDescription: event.target.value })}
                value={formValues.safeguardsDescription}
                variant='outlined'
              />
              <TextField
                disabled={readOnly}
                fullWidth
                label={t(messages.sections.safeguards.right_to_object)}
                margin='normal'
                minRows={4}
                multiline
                onChange={event => patchFormValues({ rightToObjectHandling: event.target.value })}
                value={formValues.rightToObjectHandling}
                variant='outlined'
              />
            </Box>

            <Box className={styles.apaSection}>
              <div className={styles.reviewEyebrow}>SECTION 3</div>
              <Typography gutterBottom variant='h3'>
                {t(messages.sections.conclusion.title)}
              </Typography>
              <FormControl disabled={readOnly} error={Boolean(validationErrors.conclusion)} fullWidth margin='normal' variant='outlined'>
                <InputLabel>{t(messages.sections.conclusion.conclusion)}</InputLabel>
                <Select
                  label={t(messages.sections.conclusion.conclusion)}
                  onChange={event => patchFormValues({ conclusion: event.target.value as typeof formValues.conclusion })}
                  value={formValues.conclusion}
                >
                  {LIA_CONCLUSION_OPTIONS.map(option => (
                    <MenuItem key={option} value={option}>
                      {t(messages.conclusions[option])}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.conclusion && <FormHelperText>{validationErrors.conclusion}</FormHelperText>}
              </FormControl>
              <TextField
                disabled={readOnly}
                fullWidth
                label={t(messages.sections.conclusion.comment)}
                margin='normal'
                minRows={4}
                multiline
                onChange={event => patchFormValues({ conclusionComment: event.target.value })}
                value={formValues.conclusionComment}
                variant='outlined'
              />

              <Box
                className={styles.reviewPanel}
                style={
                  currentValidationStatus === 'valid'
                    ? { backgroundColor: '#f3fbf8', borderColor: '#b8e2cb' }
                    : { backgroundColor: '#fff3f2', borderColor: '#f0bcbc' }
                }
              >
                <Typography gutterBottom variant='h4'>
                  {t(messages.validation_status.title, {
                    status: t(messages.validation_status[currentValidationStatus]),
                  })}
                </Typography>
                <Typography color='textSecondary' variant='body2'>
                  {currentValidationStatus === 'valid'
                    ? t(messages.validation_status.valid_body)
                    : t(messages.validation_status.invalid_body)}
                </Typography>
              </Box>

              {completenessItems.some(item => !item.complete) && (
                <Box className={styles.errorBanner} style={{ marginTop: 12 }}>
                  <Typography gutterBottom variant='subtitle2'>
                    {t(messages.completeness.title)}
                  </Typography>
                  {completenessItems
                    .filter(item => !item.complete)
                    .map(item => (
                      <Typography key={item.id} variant='body2'>
                        · {t(messages.completeness[item.id as keyof typeof messages.completeness] ?? item.id)}
                      </Typography>
                    ))}
                </Box>
              )}

              {!readOnly && (
                <div className={styles.reviewActions} style={{ justifyContent: 'flex-end', marginTop: 16 }}>
                  <ColoredButton
                    className=''
                    customColor='secondary'
                    disabled={isSaving || isSoftDeleting}
                    icon={false}
                    onClick={handleSaveClick}
                    showLoader={isSaving}
                    textLike={false}
                    variant='outlined'
                  >
                    {t(messages.save)}
                  </ColoredButton>
                </div>
              )}
            </Box>
          </section>
        </div>

        <SoftDeleteLiaModal
          loading={isSoftDeleting}
          onClose={() => setSoftDeleteOpen(false)}
          onConfirm={handleSoftDeleteConfirm}
          open={softDeleteOpen}
        />
      </>
    );
  }

  return (
    <ErrorBoundary>
      <Container maxWidth='lg'>
        <ManagePageHeader
          backButton
          noBorder
          onBack={() => handleNavigation()}
          rightAdornment={
            detail && !readOnly ? (
              <Box className={styles.headerActions}>
                <ColoredButton
                  className=''
                  customColor='secondary'
                  disabled={isSaving || isSoftDeleting}
                  icon={false}
                  onClick={handleSaveClick}
                  showLoader={isSaving}
                  textLike={false}
                  variant='outlined'
                >
                  {t(messages.save)}
                </ColoredButton>
                <ColoredButton
                  className=''
                  customColor='grey'
                  disabled={isSaving || isSoftDeleting}
                  icon={false}
                  onClick={() => setSoftDeleteOpen(true)}
                  showLoader={false}
                  textLike={false}
                  variant='outlined'
                >
                  {t(messages.soft_delete.action)}
                </ColoredButton>
              </Box>
            ) : undefined
          }
          subtitle={t(messages.page_subtitle, { purpose: detail?.purposeContext ?? '' })}
        >
          {t(messages.page_title)}
        </ManagePageHeader>
        {NavigationPrompt}
        {content}
      </Container>
    </ErrorBoundary>
  );
};

export default PurposeLiaPage;
